import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type {
  MessageCustomFieldsForViewQuery,
  MessageCustomFieldsForViewQueryVariables,
  CustomFieldDefinitionsQuery,
  CustomFieldDefinitionsQueryVariables
} from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import { getMessageCustomFieldsAndValuesForView } from '../MessageCustomFieldsHelper';
import messageCustomFieldsQuery from './MessageCustomFieldsForView.query.graphql';
import localStyles from './MessageCustomFields.module.pcss';
import customFieldDefinitionsQuery from '@aurora/shared-client/helpers/custom/CustomFieldDefinitions.query.graphql';

interface Props {
  /**
   * The message id to display the custom fields for.
   */
  messageId: string;
}

/**
 * Message custom fields.
 *
 * @constructor
 *
 * @author Lucas Fernandez
 */
const MessageCustomFields: React.FC<React.PropsWithChildren<Props>> = ({ messageId }: Props) => {
  const cx = useClassNameMapper(localStyles);

  const { formatMessageWithFallback } = useTranslation(EndUserComponent.MESSAGE_CUSTOM_FIELDS);

  function getLabelForCustomField(customFieldName: string): string {
    return formatMessageWithFallback(customFieldName, 'CustomField.default.label', {
      name: customFieldName
    });
  }

  const { loading: messageLoading, data: messageContext } = useQueryWithTracing<
    MessageCustomFieldsForViewQuery,
    MessageCustomFieldsForViewQueryVariables
  >(module, messageCustomFieldsQuery, {
    variables: {
      id: messageId
    }
  });
  const customFieldsValues = messageContext
    ? getMessageCustomFieldsAndValuesForView(messageContext.message?.customFields)
    : {};

  const customFieldsKeys = Object.keys(customFieldsValues);
  const hasCustomFields = customFieldsKeys.length > 0;
  const { loading: fieldDefinitionsLoading, data: fieldDefinitions } = useQueryWithTracing<
    CustomFieldDefinitionsQuery,
    CustomFieldDefinitionsQueryVariables
  >(module, customFieldDefinitionsQuery, {
    variables: {
      fieldNames: customFieldsKeys
    },
    skip: messageLoading || !hasCustomFields
  });

  if (messageLoading || fieldDefinitionsLoading) {
    return null;
  }

  const customFieldDefinitions: { name: string; viewable: boolean }[] =
    fieldDefinitions?.customFieldDefinitions?.map(field => ({
      name: field.definition?.name ?? '',
      viewable: field.definition?.viewable ?? true
    })) ?? [];

  return (
    <>
      {hasCustomFields && (
        <div
          className={cx('lia-g-my-20 lia-custom-fields-container')}
          data-testid="MessageCustomFields"
        >
          {customFieldsKeys.map(name => {
            const label: string = getLabelForCustomField(name);
            const value: string = customFieldsValues[name];
            const customFieldDefinition = customFieldDefinitions.find(field => field.name === name);
            const isViewable = customFieldDefinition ? customFieldDefinition.viewable : true;
            if (isViewable) {
              return (
                <span key={name} className={cx('lia-custom-field')}>
                  {label + ': ' + value}
                </span>
              );
            }
            return null;
          })}
        </div>
      )}
    </>
  );
};

export default MessageCustomFields;
